// Reminders — the one screen where the game asks for the phone's attention.
//
// The rules that make it a screen and not a prompt on the first chore:
//   * Nothing is asked on boot, on a chore, or on a timer. The permission
//     dialog is the browser's, and the browser only allows it to be triggered
//     from a tap the player just made. So the button IS the request.
//   * The player is told what will arrive, how often, and that a grown-up can
//     turn it off from this same screen, before the tap that asks.
//   * Every refusal has its own sentence. "Nothing happened" is not an outcome
//     the player can act on, and a parent who says no deserves to know how to
//     say yes again.
//
// It is a route, not a sheet: the answer is a standing setting, and settings that
// change the phone belong in the stack where Back works (PLAN-03 §4).

import { t } from '../../i18n';
import {
  currentPushSubscription,
  pushSupport,
  resolvePushUi,
  subscribeToPush,
  unsubscribeFromPush,
  type PushOutcome,
  type PushRefusal,
  type PushUiState,
} from '../../net/push';
import { SupabaseClient } from '../../net/supabase';
import { button, div, h, paint } from '../dom';
import type { Screen, ScreenContext } from '../router';

interface ScreenState {
  checked: boolean;
  subscribed: boolean;
  /** One calm sentence about the last attempt, or null. */
  messageKey: string | null;
  busy: boolean;
}

export function notificationsScreen(): Screen<HTMLElement> {
  // Repainting a screen the player has already backed out of is harmless in the
  // DOM and wrong in spirit: the answer they gave has moved on. The subscription
  // lookup is a round trip to the device, so it can land after the pop.
  let teardown: (() => void) | null = null;

  return {
    id: 'notifications',
    // Opaque: the renderer pauses behind anything that is not the diorama.
    showsStage: false,
    dispose() {
      teardown?.();
      teardown = null;
    },
    render: (ctx: ScreenContext<HTMLElement>) => {
      const client = new SupabaseClient();
      let alive = true;
      teardown = () => {
        alive = false;
      };
      const state: ScreenState = { checked: false, subscribed: false, messageKey: null, busy: false };
      const body = div({ class: 'cf-stack' });
      const screen = div(
        { class: 'cf-screen' },
        h('h1', { class: 'cf-display', text: t('push.title') }),
        body,
        div(
          { class: 'cf-screen__foot' },
          button({
            class: 'cf-btn cf-btn--primary',
            text: t('common.back'),
            on: { click: () => ctx.router.back() },
          }),
        ),
      );

      const ui = (): PushUiState =>
        resolvePushUi({
          support: pushSupport(),
          signedIn: client.signedIn,
          subscribed: state.subscribed,
          checked: state.checked,
        });

      const repaint = (): void => {
        if (!alive) return;
        const view = ui();
        paint(body, `${view.kind}|${state.messageKey ?? ''}|${String(state.busy)}`, () =>
          div(
            { class: 'cf-stack' },
            h('p', { class: 'cf-sub', text: t('push.why') }),
            div(
              { class: `cf-notice cf-notice--${tone(view)}` },
              h('span', { class: 'cf-notice__text', text: t(view.detailKey) }),
            ),
            actionRow(view),
            h('p', { class: 'cf-small cf-muted', text: t('push.parentNote') }),
            state.messageKey
              ? h('p', { class: 'cf-small', text: t(state.messageKey) })
              : null,
          ),
        );
      };

      const actionRow = (view: PushUiState): HTMLElement | null => {
        if (view.action === 'none' || state.busy) return null;
        if (view.action === 'enable') {
          return button({
            class: 'cf-btn cf-btn--primary',
            text: t('push.enable'),
            on: {
              click: () => {
                void (async () => {
                  state.busy = true;
                  state.messageKey = null;
                  repaint();
                  const outcome = await subscribeToPush(client);
                  if (!alive) return;
                  state.busy = false;
                  state.checked = true;
                  state.subscribed = outcome.status === 'subscribed';
                  state.messageKey = messageFor(outcome);
                  repaint();
                })();
              },
            },
          });
        }
        return button({
          class: 'cf-btn cf-btn--outline',
          text: t('push.disable'),
          on: {
            click: () => {
              void (async () => {
                state.busy = true;
                state.messageKey = null;
                repaint();
                const outcome = await unsubscribeFromPush(client);
                state.busy = false;
                state.checked = true;
                state.subscribed = false;
                state.messageKey = messageFor(outcome);
                repaint();
              })();
            },
          },
        });
      };

      // The real state is the device's, so ask the device. Until it answers, the
      // screen says "checking" and shows no button: an enable button that appears
      // over a phone that is already subscribed is a lie the player acts on.
      void (async () => {
        const subscription = await currentPushSubscription();
        if (!alive) return;
        state.subscribed = subscription !== null;
        state.checked = true;
        repaint();
      })();
      repaint();

      screen.tabIndex = -1;
      return screen;
    },
  };
}

function tone(view: PushUiState): 'info' | 'good' | 'warn' | 'danger' {
  switch (view.kind) {
    case 'on':
      return 'good';
    case 'off':
    case 'checking':
      return 'info';
    case 'denied':
      return 'danger';
    default:
      // Unsupported, unconfigured, no account: a reason, not an emergency.
      return 'warn';
  }
}

/** One sentence per refusal, so no "no" is ever a silent nothing. */
function refusalMessage(reason: PushRefusal): string {
  switch (reason) {
    case 'unsupported':
      return 'push.unsupported';
    case 'unconfigured':
      return 'push.notConfigured';
    case 'denied':
      return 'push.denied';
    case 'needs_account':
      return 'push.needsAccount';
    case 'not_configured':
      return 'push.serverNotConfigured';
    case 'offline':
      return 'push.offline';
  }
}

function messageFor(outcome: PushOutcome): string {
  if (outcome.status === 'subscribed') return 'push.on';
  if (outcome.status === 'unsubscribed') return 'push.offDone';
  if (outcome.status === 'refused') return refusalMessage(outcome.reason);
  if (outcome.reason === 'offline') return 'push.offline';
  if (outcome.reason === 'not_configured') return 'push.serverNotConfigured';
  return 'push.failed';
}

/** The settings row that opens this screen. */
export function notificationsRow(onOpen: () => void): HTMLElement {
  const support = pushSupport();
  const sub = support === 'unsupported' || support === 'unconfigured' ? t('push.rowOff') : t('push.rowSub');
  return button(
    { class: 'cf-row', on: { click: onOpen } },
    h(
      'span',
      { class: 'cf-stack cf-stack--tight cf-grow' },
      h('span', { class: 'cf-label', text: t('push.title') }),
      h('span', { class: 'cf-small cf-muted', text: sub }),
    ),
  );
}
